/* eslint-disable max-lines */
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthenticationType } from 'expo-local-authentication';
import {
  Stack,
  useFocusEffect,
  useGlobalSearchParams,
  useRouter,
} from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import VaroTurnstile from 'features/auth/components/VaroTurnstile/VaroTurnstile';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AppState,
  BackHandler,
  Keyboard,
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  setPreventBiometricAutoLogin,
  shouldPreventBiometricAutoLogin,
} from 'services/deeplinks/deeplinkManager';
import {
  getUnsupportedDialogDetails,
  openSettings,
  shouldShowDialogDetails,
} from 'services/forceUpdate/helpers';
import FDICDisclaimer from 'shared/components/FDICDisclaimer/FDICDisclaimer';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Button from 'shared/components/vxl/Button/Button';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import VaroLogo from 'shared/components/vxl/VaroLogo/VaroLogo';
import { Headers } from 'shared/constants/headers';
import { isIOS, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import {
  authenticateWithBiometric,
  deleteUserRegisteredKey,
  getBiometricType,
  getBiometricUserId,
  isMigratedUser,
  saveBiometricToggleSetting,
  shouldShowBiometricButton,
} from 'shared/utils/biometricAuth/biometricAuth';
import hasCaptchaToken from 'shared/utils/hasCaptchaToken';
import { addTag, logBreadcrumb, logError } from 'shared/utils/sentry/sentry';
import { SentryTags } from 'shared/utils/sentry/sentry.constants';
import {
  clearPhoneNumberFormatting,
  getLocalPhoneDigits,
  PHONE_ALLOWED_CHARACTERS_REGEX,
} from 'shared/utils/string/phone';
import z from 'zod';

import { loginSchema } from './Login.schema';

const MAX_LOGIN_ATTEMPTS_ERROR_CODE = '10';

const Login = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('auth', { keyPrefix: 'login' });
  const [isBiometric, setBioMetric] = useState(true);
  const [isPhoneNumber, setIsPhoneNumber] = useState(false);
  const [isLoginProcessActive, setIsLoginProcessActive] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [loginAttempt, setLoginAttempt] = useState(0);
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [xVaroCToken, setXVaroCToken] = useState<string>('');
  const [biometricType, setBiometricType] = useState<
    AuthenticationType[] | null
  >(null);

  const forceUpdateDialogDetails = isWeb ? null : getUnsupportedDialogDetails();

  const [forceUpdateModalVisible, setForceUpdateModalVisible] = useState(
    shouldShowDialogDetails(),
  );

  const handleHelpClicked = usePressableAnalytics({
    analyticsId: 'helpClicked',
  });

  const handleForgotPasswordClicked = usePressableAnalytics({
    analyticsId: 'forgotPasswordClicked',
    customAttributes: {
      login_attempt: loginAttempt.toString(),
      login_method: 'password',
    },
  });

  const handlePasswordMaskClicked = usePressableAnalytics({
    analyticsId: 'passwordMaskClicked',
  });

  const handleUsernameFieldError = usePressableAnalytics({
    analyticsId: 'usernameFieldError',
    customAttributes: {
      error_type: 'incorrect_format',
      error_description: 'Enter a valid email or phone number',
    },
  });

  const handlePasswordFieldError = usePressableAnalytics({
    analyticsId: 'passwordFieldError',
    customAttributes: {
      error_type: 'null_password',
      error_description: 'Required',
    },
  });

  const {
    handleLogin,
    isLoggedIn,
    loginError,
    loginRequestStatusCode,
    resetAuthState,
    handleBiometricLogin,
    isLoginLoading,
    showBiometricButtonAction,
    setShowBiometricButtonAction,
    setErrorMessage,
    loginPostErrorMessage,
    loginErrorCode,
    loginPostErrorTitle,
    tokenErrorType,
  } = useContext<AuthContextProps>(AuthContext);

  const handleBackPress = useCallback(() => {
    return isLoginProcessActive;
  }, [isLoginProcessActive]);
  const params = useGlobalSearchParams();

  useFocusEffect(
    useCallback(() => {
      if (!isLoggedIn) {
        void resetAuthState();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => backHandler.remove();
    }, [handleBackPress]),
  );

  const exitLogin = () => {
    BackHandler.exitApp();
    return true;
  };

  useBackHandler(exitLogin);

  const loginFailed = usePressableAnalytics({
    analyticsId: 'loginFailed',
    customAttributes: {
      login_method: isBiometric ? 'biometrics' : 'password',
      error_type: loginPostErrorTitle as string,
      error_description: loginPostErrorMessage ?? loginError,
      username_type: isPhoneNumber ? 'phone' : 'email',
      status_code: loginRequestStatusCode,
      token_error_type: tokenErrorType ?? '',
    },
  });

  const onSignupButtonAction = () => {
    return router.push({ pathname: '/signup', params });
  };

  const onHelpButtonAction = () => {
    handleHelpClicked();
    router.push('/help');
  };

  // Check for biometric auto-login prevention (from deeplinks like password reset)
  useEffect(() => {
    if (shouldPreventBiometricAutoLogin()) {
      setShowBiometricButtonAction(false);
      setPreventBiometricAutoLogin(false);
    }
  }, [setShowBiometricButtonAction]);

  useEffect(() => {
    if (loginError && loginError.trim() !== '') {
      setModalVisible(true);
      loginFailed();
    } else {
      setLoginAttempt(0);
    }
  }, [loginError, loginPostErrorTitle, loginPostErrorMessage, loginFailed]);

  const { fetchWhereToGo, isLoading: isWhereToGoLoading } = useWhereToGo();

  const isEmailInput = (email: string) => {
    return z.email().safeParse(email).success;
  };

  const isPhoneInput = (phone: string) => {
    let cleaned = phone.replace(/[\s().-]/g, '');

    if (cleaned.length === 10 && !cleaned.startsWith('+')) {
      cleaned = `+1${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      cleaned = `+${cleaned}`;
    }

    return z.e164().safeParse(cleaned).success;
  };

  const normalizeEmailOrPhoneInput = (input: string) => {
    const trimmedInput = input.trim();
    const isPotentialPhoneInput =
      trimmedInput.length > 0 &&
      PHONE_ALLOWED_CHARACTERS_REGEX.test(trimmedInput) &&
      !trimmedInput.includes('@');

    if (isPotentialPhoneInput) {
      return getLocalPhoneDigits(trimmedInput);
    }

    return trimmedInput;
  };

  const { control, setValue, getValues, trigger, reset, formState } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (formState.errors.emailOrPhone) {
      handleUsernameFieldError();
    }
    if (formState.errors.password) {
      handlePasswordFieldError();
    }
  }, [formState, handlePasswordFieldError, handleUsernameFieldError]);

  useEffect(() => {
    const handleAppStateChange = (nextAppState: string) => {
      if (nextAppState === 'background') {
        setValue('password', '');
      }
    };

    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange,
    );

    return () => {
      subscription.remove();
    };
  }, [reset, setValue]);

  const loginByBiometricSubmitted = usePressableAnalytics({
    analyticsId: 'loginClicked',
    customAttributes: {
      username_type: isPhoneNumber ? 'phone' : 'email',
      login_method: 'biometrics',
    },
  });

  const loginByPasswordSubmitted = usePressableAnalytics({
    analyticsId: 'loginClicked',
    customAttributes: {
      username_type: isPhoneNumber ? 'phone' : 'email',
      login_method: 'password',
    },
  });

  const shownBiometricOption = usePressableAnalytics({
    analyticsId: 'shownBiometric',
  });

  const biometricCancelledAnalytics = usePressableAnalytics({
    analyticsId: 'biometricCancelled',
  });

  const { isFeatureEnabled } = useStatsig();
  const isCloudflareTurnstileEnabledForNative = isFeatureEnabled(
    FeatureFlagKey.ENABLE_CLOUDFLARE_TURNSTILE_NATIVE,
  );
  useFocusEffect(
    useCallback(() => {
      if (isLoggedIn) {
        logBreadcrumb({
          level: 'info',
          message: 'Logged in',
        });
        void fetchWhereToGo();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]),
  );

  useEffect(() => {
    if (!isWeb) {
      const checkForMigratedOrRegisteredUser = async () => {
        try {
          const checkBiometricUserID = async () => {
            const biometricUserID = (await getBiometricUserId()) ?? '';
            if (
              isEmailInput(biometricUserID) ||
              isPhoneInput(biometricUserID)
            ) {
              setValue('emailOrPhone', biometricUserID);
            }
          };

          if (await isMigratedUser()) {
            const checkBiometricSupport = async () => {
              const showBiometricCTA = await shouldShowBiometricButton();

              setBiometricAvailable(showBiometricCTA);
              await saveBiometricToggleSetting(showBiometricCTA);
            };

            const checkBiometricType = async () => {
              const biometricType = await getBiometricType();
              if (biometricType) {
                setBiometricType(biometricType);
              }
            };

            void checkBiometricSupport();
            void checkBiometricType();
          }
          void checkBiometricUserID();
        } catch (error) {
          logBreadcrumb({
            level: 'error',
            message: 'Error checking for migrator user',
          });
          addTag(SentryTags.FRAUD, 'checkForMigratorUser');
          logError(error);
        }
      };

      void checkForMigratedOrRegisteredUser();
    }
  }, [setValue]);

  useEffect(() => {
    if (
      !isWeb &&
      biometricAvailable &&
      showBiometricButtonAction &&
      !isLoggedIn
    ) {
      void onBiometricButtonAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biometricAvailable, isWeb]);

  const onLoginButtonAction = async () => {
    if (
      !hasCaptchaToken({ xVaroCToken, isCloudflareTurnstileEnabledForNative })
    ) {
      return; // prevent onSubmitEditing if captcha token is not set
    }

    const isFormValid = await trigger();
    if (isFormValid) {
      const formData = getValues();
      const emailOrPhone = normalizeEmailOrPhoneInput(formData.emailOrPhone);
      const isLoginWithPhoneNumber = isPhoneInput(emailOrPhone);

      setIsLoginProcessActive(true);
      setBioMetric(false);
      addTag(SentryTags.FRAUD, 'loginButtonAction');
      setIsPhoneNumber(isLoginWithPhoneNumber);
      loginByPasswordSubmitted();
      logBreadcrumb({
        level: 'info',
        message: 'Login button clicked',
        data: {
          username_type: isLoginWithPhoneNumber ? 'phone' : 'email',
          login_method: 'password',
        },
      });

      if ((await getBiometricUserId()) !== emailOrPhone) {
        await deleteUserRegisteredKey();
      }

      const headers: Headers = {};

      if (xVaroCToken) {
        headers['x-varo-ctoken'] = xVaroCToken;
      }

      if (!isLoginWithPhoneNumber) {
        await handleLogin(
          {
            emailAddress: emailOrPhone,
            password: formData.password,
          },
          headers,
        );
      } else {
        await handleLogin(
          {
            emailAddress: '',
            phoneNumber: clearPhoneNumberFormatting(emailOrPhone),
            password: formData.password,
          },
          headers,
        );
      }

      setLoginAttempt(loginAttempt + 1);
      setModalVisible(true);
      setIsLoginProcessActive(false);
    }
  };

  useEffect(() => {
    if (loginErrorCode === MAX_LOGIN_ATTEMPTS_ERROR_CODE) {
      biometricCancelledAnalytics();
    }
  }, [biometricCancelledAnalytics, loginErrorCode]);

  const onBiometricButtonAction = async () => {
    const getUserID = await getBiometricUserId();
    const isPhoneLogin = isPhoneInput(getUserID ?? '');
    const isLoginWithEmail = isEmailInput(getUserID ?? '');

    if (!isLoginWithEmail && !isPhoneLogin) {
      // This means it could be stored masked email (migration case), force user to login instead of biometrics
      // https://varomoney.atlassian.net/browse/JT-129724
      await deleteUserRegisteredKey();
      setShowBiometricButtonAction(false);
      setBiometricAvailable(false);
      reset({ password: '', emailOrPhone: '' });
      return;
    }
    const getToken = await authenticateWithBiometric();

    shownBiometricOption();
    setIsLoginProcessActive(true);
    addTag(SentryTags.FRAUD, 'onBiometricButtonAction');
    setBioMetric(true);
    if (getUserID) {
      setIsPhoneNumber(isPhoneInput(getUserID));
    } else {
      setIsPhoneNumber(false);
    }

    if (getToken && getUserID) {
      loginByBiometricSubmitted();
      logBreadcrumb({
        level: 'info',
        message: 'Biometric login button clicked',
        data: {
          username_type: isPhoneNumber ? 'phone' : 'email',
          login_method: 'biometric',
        },
      });

      if (isLoginWithEmail) {
        void handleBiometricLogin({
          refreshToken: getToken,
          emailAddress: getUserID,
        });
      } else {
        void handleBiometricLogin({
          refreshToken: getToken,
          emailAddress: '',
          phoneNumber: clearPhoneNumberFormatting(getUserID),
        });
      }
    }
    setIsLoginProcessActive(false);
  };

  const insets = useSafeAreaInsets();

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: insets.bottom,
        },
      ]}
    >
      <StatusBar style="dark" />
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShown: true,
          headerShadowVisible: false,
          headerTitle: '',
          headerRight: () => (
            <View style={styles.helpButtonContainer}>
              <PressableWithDelay
                onPress={() => {
                  onHelpButtonAction();
                }}
              >
                <BodyBaseBoldText
                  style={styles.helpButton}
                  disabled={isLoginLoading}
                  numberOfLines={0}
                >
                  {translatedText('buttons.help')}
                </BodyBaseBoldText>
              </PressableWithDelay>
            </View>
          ),
        }}
      />
      <ScrollView contentContainerStyle={styles.scrollContentContainer}>
        <TouchableWithoutFeedback
          accessible={false}
          onPress={() => {
            if (!isWeb) {
              Keyboard.dismiss();
            }
          }}
        >
          <KeyboardAvoidingView
            behavior={isIOS ? 'padding' : 'height'}
            enabled={!isIOS}
            keyboardVerticalOffset={-100}
          >
            <View style={styles.logoContainer} testID="varo-logo">
              <VaroLogo width={100} height={37} />
            </View>
            <View style={styles.formFieldsContainer}>
              <Controller
                control={control}
                name="emailOrPhone"
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { error },
                }) => (
                  <VXLTextInput
                    labelText={translatedText('textField.email')}
                    errorText={error?.message}
                    onChangeText={(text) => {
                      onChange(text);
                      setIsPhoneNumber(isPhoneInput(text));
                    }}
                    value={value}
                    keyboardType="email-address"
                    autoFocus={!isWeb}
                    analyticsId="emailFieldClicked"
                    onSubmitEditing={async () => {
                      await onLoginButtonAction();
                    }}
                    onBlur={() => {
                      // Normalize once the user is done typing (rather than on
                      // every keystroke) to avoid rewriting the field value
                      // mid-typing, which can cause cursor jumps on RN
                      // controlled TextInputs.
                      const normalizedText = normalizeEmailOrPhoneInput(
                        value ?? '',
                      );
                      if (normalizedText !== value) {
                        onChange(normalizedText);
                      }
                      onBlur();
                    }}
                    autoCapitalize="none"
                    editable={!isLoginLoading}
                    autoComplete="email"
                    importantForAutofill="yes"
                    testID="text-input-textField.email"
                  />
                )}
              />
              <Controller
                control={control}
                name="password"
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { error },
                }) => {
                  return (
                    <VXLTextInput
                      labelText={translatedText('textField.password')}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      errorText={error?.message}
                      secureTextEntry={!isPasswordVisible}
                      autoCapitalize="none"
                      autoComplete="password"
                      textContentType="password"
                      autoCorrect={false}
                      editable={!isLoginLoading}
                      inputMode="text"
                      analyticsId="passwordFieldClicked"
                      importantForAutofill="yes"
                      onSubmitEditing={async () => {
                        await onLoginButtonAction();
                      }}
                      rightIcon={{
                        iconKey: !isPasswordVisible ? 'VIEW' : 'HIDE',
                        iconAccessibilityLabel: isPasswordVisible
                          ? 'eye-close'
                          : 'eye-open',
                        onPress: () => {
                          handlePasswordMaskClicked();
                          setPasswordVisible(!isPasswordVisible);
                        },
                      }}
                      testID="text-input-textField.password"
                    />
                  );
                }}
              />
            </View>
            <View style={styles.buttonContainer}>
              <View style={styles.rowContainer}>
                <PressableWithDelay
                  testID="login-forgot-password-link"
                  onPress={() => {
                    router.push('/reset-password/find-account');
                    handleForgotPasswordClicked();
                  }}
                >
                  <HyperlinkSmText disabled={isLoginLoading}>
                    {translatedText('buttons.forgotPassword')}
                  </HyperlinkSmText>
                </PressableWithDelay>

                {biometricAvailable &&
                loginErrorCode !== MAX_LOGIN_ATTEMPTS_ERROR_CODE ? (
                  <View style={styles.biometricContainer}>
                    <IconButton
                      iconKey={
                        biometricType?.[0] ===
                        AuthenticationType.FACIAL_RECOGNITION
                          ? 'FACE_ID'
                          : 'TOUCH_ID'
                      }
                      iconSize={28}
                      iconColor={theme.color.interactive.primary}
                      iconButtonAccessibilityLabel="BIOMETRIC"
                      iconOnPress={onBiometricButtonAction}
                      isIconDisabled={
                        Boolean(formState.errors?.emailOrPhone) ||
                        isLoginLoading
                      }
                    />
                    <BodySmBoldText
                      style={[
                        styles.faceIDButton,
                        isLoginLoading ? styles.faceIDButtonDisabled : null,
                      ]}
                      numberOfLines={0}
                      onPress={onBiometricButtonAction}
                      disabled={
                        Boolean(formState.errors?.emailOrPhone) ||
                        isLoginLoading
                      }
                    >
                      {biometricType?.[0] ===
                      AuthenticationType.FACIAL_RECOGNITION
                        ? translatedText('buttons.faceIDButton')
                        : translatedText('buttons.touchIDButton')}
                    </BodySmBoldText>
                  </View>
                ) : null}
              </View>
              <Button
                testID="login-submit-button"
                label={translatedText('buttons.logIn')}
                onPress={onLoginButtonAction}
                isDisabled={
                  !hasCaptchaToken({
                    xVaroCToken,
                    isCloudflareTurnstileEnabledForNative,
                  })
                }
                isLoading={isLoginLoading || isWhereToGoLoading}
                size="LARGE"
                type="PRIMARY"
                width="FULL"
              />
              <Button
                label={translatedText('buttons.signUp')}
                onPress={onSignupButtonAction}
                isDisabled={isLoginLoading}
                analyticsId="signupClicked"
                size="LARGE"
                type="SECONDARY"
                width="FULL"
              />
            </View>
          </KeyboardAvoidingView>
        </TouchableWithoutFeedback>
        <VaroTurnstile
          setXVaroCToken={setXVaroCToken}
          xVaroCToken={xVaroCToken}
        />

        <FDICDisclaimer />
        {forceUpdateModalVisible ? (
          <Dialog
            titleText={forceUpdateDialogDetails?.title}
            messageText={forceUpdateDialogDetails?.message}
            isDialogVisible={forceUpdateModalVisible}
            isIOSCaptiveDialog
            setModalVisible={setForceUpdateModalVisible}
            dialogButtonProps={[
              {
                label: translatedText('buttons.dismiss'),
                onClick: () => {
                  setForceUpdateModalVisible(false);
                },
              },
              {
                label: translatedText('buttons.update'),
                onClick: () => {
                  openSettings();
                  setForceUpdateModalVisible(false);
                },
              },
            ]}
          />
        ) : null}
      </ScrollView>
      {loginError && loginError.trim() !== '' ? (
        <Dialog
          isDialogVisible={modalVisible}
          setModalVisible={setModalVisible}
          titleText={loginPostErrorTitle}
          messageText={
            loginPostErrorMessage && loginPostErrorMessage.trim()
              ? loginPostErrorMessage
              : translatedText('error.dialog.message')
          }
          dialogButtonProps={[
            {
              label: translatedText('buttons.ok'),
              testID: 'login-error-dialog-ok-button',
              onClick: () => {
                setErrorMessage();
                setModalVisible(false);
              },
            },
          ]}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  biometricContainer: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.s,
    justifyContent: 'flex-end',
  },
  buttonContainer: {
    gap: theme.spacing.m,
    marginBottom: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  faceIDButton: {
    color: theme.color.interactive.primary,
  },
  faceIDButtonDisabled: {
    color: theme.color.text.muted,
  },
  formFieldsContainer: {
    gap: theme.spacing.m,
  },
  helpButton: {
    color: theme.color.interactive.primary,
  },
  helpButtonContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  logoContainer: {
    alignItems: 'center',
    alignSelf: 'center',
    flex: 1,
    flexShrink: 1,
    paddingBottom: 64,
    paddingTop: 24,
  },
  rowContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.m,
  },
  scrollContentContainer: {
    flexGrow: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default Login;
